/**
 * Workspace hooks (PR4a) — TanStack Query over workItemsApi / workspaceMetaApi.
 *
 * Mirrors announcements.ts / suggestedEdits.ts conventions: thin, server-
 * authoritative, mutations invalidate the affected list + detail keys. The
 * page gates these behind canViewWorkspace / canManageWorkItems (the server
 * re-checks); these hooks carry no authorization logic.
 */

import { useMutation, useQuery, useQueryClient } from '@tanstack/react-query';
import {
  workItemsApi,
  workspaceMetaApi,
  recurringSeriesApi,
  type ListWorkItemsParams,
  type ListWorkItemsResponse,
  type WorkItemDetail,
  type WorkItemRow,
  type MoveStageInput,
  type UpdateWorkItemInput,
  type CreateWorkItemInput,
  type CreateRecurringSeriesInput,
  type UpdateRecurringSeriesInput,
  type CreateRecurringSeriesResult,
  type AttachRecurrenceInput,
  type RecurringSeriesRow,
  type PipelineStageWire,
  type AssignCareManagerWire,
  type AssignUnitWire,
  type WorkspaceMemberWire,
  type ConversionMetricsResponse,
  type ConversionDrillResponse,
} from '@/lib/api';

const STALE_MS = 30_000;

export function workItemsKey(slug: string, params?: ListWorkItemsParams): readonly unknown[] {
  return params ? (['work-items', slug, params] as const) : (['work-items', slug] as const);
}
export function workItemDetailKey(slug: string, id: string): readonly unknown[] {
  return ['work-item', slug, id] as const;
}
/** Card attachments (0162): the drawer section's list read, per card. */
export function workItemAttachmentsKey(slug: string, id: string): readonly unknown[] {
  return ['work-item-attachments', slug, id] as const;
}
export function pipelineStagesKey(slug: string, processId?: string): readonly unknown[] {
  return processId ? (['workspace-stages', slug, processId] as const) : (['workspace-stages', slug] as const);
}
export function assignOptionsKey(slug: string): readonly unknown[] {
  return ['workspace-assign-options', slug] as const;
}
export function membersKey(slug: string): readonly unknown[] {
  return ['workspace-members', slug] as const;
}

export function useWorkItems(slug: string, params: ListWorkItemsParams, opts: { enabled?: boolean } = {}) {
  const query = useQuery<ListWorkItemsResponse>({
    queryKey: workItemsKey(slug, params),
    queryFn: ({ signal }) => workItemsApi.list(slug, params, signal),
    staleTime: STALE_MS,
    enabled: opts.enabled ?? true,
  });
  return {
    rows: query.data?.rows ?? [],
    total: query.data?.total ?? 0,
    isLoading: query.isLoading,
    isError: query.isError,
    error: query.error,
    refetch: query.refetch,
  };
}

// My Tasks polish: the agenda's "Completed" section (last 30 days, newest first).
export function completedTasksKey(slug: string): readonly unknown[] {
  return ['completed-tasks', slug] as const;
}

export function useCompletedTasks(slug: string, opts: { enabled?: boolean } = {}) {
  const query = useQuery<import('@/lib/api').ListCompletedTasksResponse>({
    queryKey: completedTasksKey(slug),
    queryFn: ({ signal }) => workItemsApi.completedTasks(slug, {}, signal),
    staleTime: STALE_MS,
    enabled: opts.enabled ?? true,
  });
  return {
    rows: query.data?.rows ?? [],
    isLoading: query.isLoading,
    isError: query.isError,
  };
}

export function useWorkItemDetail(slug: string, id: string | null, opts: { enabled?: boolean } = {}) {
  const query = useQuery<WorkItemDetail>({
    queryKey: workItemDetailKey(slug, id ?? ''),
    queryFn: ({ signal }) => workItemsApi.get(slug, id!, signal),
    staleTime: STALE_MS,
    enabled: (opts.enabled ?? true) && id !== null,
  });
  return query;
}

export function usePipelineStages(slug: string, opts: { enabled?: boolean; processId?: string } = {}) {
  const query = useQuery<{ stages: PipelineStageWire[]; request_id: string }>({
    queryKey: pipelineStagesKey(slug, opts.processId),
    queryFn: ({ signal }) => workspaceMetaApi.stages(slug, opts.processId, signal),
    staleTime: 5 * 60_000, // stages rarely change
    enabled: opts.enabled ?? true,
  });
  return { stages: query.data?.stages ?? [], isLoading: query.isLoading, isError: query.isError };
}

export function caseloadKey(slug: string): readonly unknown[] {
  return ['workspace-caseload', slug] as const;
}

export function useCaseload(slug: string, opts: { enabled?: boolean } = {}) {
  const query = useQuery<{ care_managers: import('@/lib/api').CaseloadCmWire[]; request_id: string }>({
    queryKey: caseloadKey(slug),
    queryFn: ({ signal }) => workspaceMetaApi.caseload(slug, signal),
    staleTime: STALE_MS,
    enabled: opts.enabled ?? true,
  });
  return { careManagers: query.data?.care_managers ?? [], isLoading: query.isLoading, isError: query.isError, error: query.error, refetch: query.refetch };
}

export function conversionKey(slug: string, params: { from: string; to: string }): readonly unknown[] {
  return ['workspace-conversion', slug, params] as const;
}

export function useConversion(slug: string, params: { from: string; to: string }, opts: { enabled?: boolean } = {}) {
  const query = useQuery<ConversionMetricsResponse>({
    queryKey: conversionKey(slug, params),
    queryFn: ({ signal }) => workspaceMetaApi.conversion(slug, params, signal),
    staleTime: STALE_MS,
    enabled: opts.enabled ?? true,
  });
  return {
    metrics: query.data ?? null,
    isLoading: query.isLoading,
    isError: query.isError,
    error: query.error,
    refetch: query.refetch,
  };
}

export function conversionDrillKey(
  slug: string,
  params: { from: string; to: string; segment: string },
): readonly unknown[] {
  return ['workspace-conversion-drill', slug, params] as const;
}

/** The audit drill for one clicked segment — enabled only while a segment is open. */
export function useConversionDrill(
  slug: string,
  params: { from: string; to: string; segment: string },
  opts: { enabled?: boolean } = {},
) {
  const query = useQuery<ConversionDrillResponse>({
    queryKey: conversionDrillKey(slug, params),
    queryFn: ({ signal }) => workspaceMetaApi.conversionDrill(slug, params, signal),
    staleTime: STALE_MS,
    enabled: opts.enabled ?? true,
  });
  return {
    drill: query.data ?? null,
    isLoading: query.isLoading,
    isError: query.isError,
    error: query.error,
    refetch: query.refetch,
  };
}

/** Every intake list key the drawer renders — the shape callers may assume. */
const INTAKE_LIST_KEYS = [
  'service_requested', 'contact_relationship', 'contact_method', 'paperwork_method',
  'close_reason', 'referrer_type', 'intake_call_by',
] as const;

/**
 * PER-KEY normalization of the wire's `lists` (design pass 2026-07-25 —
 * the drawer white-screen). The old fallback replaced the lists object
 * WHOLESALE (`data?.lists ?? {…all seven empty…}`), which only helps when
 * `lists` is entirely absent. A PARTIAL payload — an older API, a tenant
 * whose lookup rows were pruned, a list that fell out of the config —
 * left `lists` truthy with a missing key, so the fallback never fired and
 * `lists.referrer_type.map(…)` threw. That is a render-time crash inside
 * the drawer's subtree: the whole page went white, with no way back but a
 * reload. Every key is now guaranteed to be an array whatever the wire
 * sends, so a missing list degrades to an empty picker (and the
 * select-or-free-text fields fall through to free text) instead of a
 * white screen.
 */
export function normalizeIntakeLists(
  wire: Partial<Record<string, unknown>> | undefined,
): Record<import('@/lib/api').IntakeListKey, string[]> {
  const out = {} as Record<import('@/lib/api').IntakeListKey, string[]>;
  for (const key of INTAKE_LIST_KEYS) {
    const v = wire?.[key];
    out[key] = Array.isArray(v) ? (v as string[]).filter((o): o is string => typeof o === 'string') : [];
  }
  return out;
}

export function useIntakeOptions(slug: string, opts: { enabled?: boolean } = {}) {
  const query = useQuery<import('@/lib/api').IntakeOptions & { request_id: string }>({
    queryKey: ['workspace-intake-options', slug],
    queryFn: ({ signal }) => workspaceMetaApi.intakeOptions(slug, signal),
    staleTime: 5 * 60_000,
    enabled: opts.enabled ?? true,
  });
  return {
    lists: normalizeIntakeLists(query.data?.lists as Partial<Record<string, unknown>> | undefined),
    defaults: query.data?.defaults ?? {},
    freeText: query.data?.free_text ?? {},
    isLoading: query.isLoading,
  };
}

export function useCloseIntake(slug: string) {
  const invalidate = useInvalidate(slug);
  return useMutation({
    mutationFn: (vars: { id: string; reason: string | null }) => workItemsApi.close(slug, vars.id, vars.reason),
    onSuccess: (_d, vars) => invalidate(vars.id),
  });
}

export function useReopenIntake(slug: string) {
  const invalidate = useInvalidate(slug);
  return useMutation({
    mutationFn: (id: string) => workItemsApi.reopen(slug, id),
    onSuccess: (_d, id) => invalidate(id),
  });
}

// Brief 3 PR3 — unassign the CM (re-queues the case to Ready-for-Assignment).
export function useUnassignCm(slug: string) {
  const invalidate = useInvalidate(slug);
  return useMutation({
    mutationFn: (id: string) => workItemsApi.unassignCm(slug, id),
    onSuccess: (_d, id) => invalidate(id),
  });
}

// Brief 3 PR3 — reassign the CM in place on an Assigned case (wrong-CM fix).
export function useReassignCm(slug: string) {
  const invalidate = useInvalidate(slug);
  return useMutation({
    mutationFn: (vars: { id: string; rosterId: string }) => workItemsApi.reassignCm(slug, vars.id, vars.rosterId),
    onSuccess: (_d, vars) => invalidate(vars.id),
  });
}

export function useAssignOptions(slug: string, opts: { enabled?: boolean } = {}) {
  const query = useQuery<{
    care_managers: AssignCareManagerWire[];
    spice_therapists?: AssignCareManagerWire[];
    units: AssignUnitWire[];
    request_id: string;
  }>({
    queryKey: assignOptionsKey(slug),
    queryFn: ({ signal }) => workspaceMetaApi.assignOptions(slug, signal),
    staleTime: 5 * 60_000,
    enabled: opts.enabled ?? true,
  });
  return {
    careManagers: query.data?.care_managers ?? [],
    // 0137 (WS-4 D3): active does_spice roster — the SPICE-therapist picker.
    spiceTherapists: query.data?.spice_therapists ?? [],
    units: query.data?.units ?? [],
    isLoading: query.isLoading,
    isError: query.isError,
    error: query.error,
  };
}

export function useWorkspaceMembers(slug: string, opts: { enabled?: boolean } = {}) {
  const query = useQuery<{ members: WorkspaceMemberWire[]; request_id: string }>({
    queryKey: membersKey(slug),
    queryFn: ({ signal }) => workspaceMetaApi.members(slug, signal),
    staleTime: 5 * 60_000,
    enabled: opts.enabled ?? true,
  });
  return { members: query.data?.members ?? [], isLoading: query.isLoading };
}

/**
 * Invalidate every work-items list + (optionally) one detail, AND the bell's
 * work-notifications query — an assign/move/patch can change who owns an item
 * (its assigned/overdue feed), so the badge must refetch too.
 */
function useInvalidate(slug: string) {
  const qc = useQueryClient();
  return (id?: string) => {
    void qc.invalidateQueries({ queryKey: ['work-items', slug] });
    void qc.invalidateQueries({ queryKey: ['work-notifications', slug] });
    // My Tasks polish: a done-flip / restore / recurring advance changes the
    // agenda's Completed section, so it refetches with the lists.
    void qc.invalidateQueries({ queryKey: ['completed-tasks', slug] });
    if (id) void qc.invalidateQueries({ queryKey: workItemDetailKey(slug, id) });
  };
}

export function useMoveStage(slug: string) {
  const invalidate = useInvalidate(slug);
  return useMutation({
    mutationFn: (vars: { id: string; input: MoveStageInput }) => workItemsApi.moveStage(slug, vars.id, vars.input),
    onSuccess: (_d, vars) => invalidate(vars.id),
  });
}

export function usePatchWorkItem(slug: string) {
  const invalidate = useInvalidate(slug);
  const qc = useQueryClient();
  return useMutation({
    mutationFn: (vars: { id: string; edits: UpdateWorkItemInput }) => workItemsApi.update(slug, vars.id, vars.edits),
    // Optimistic merge (founder bug 2026-07-10: pick a category and the
    // drawer select kept reading "None" until reopen — the controlled
    // value waited on the refetch). Merge the edited fields into the
    // detail cache immediately; the invalidate below still reconciles
    // with the server, and an error restores the snapshot.
    onMutate: async (vars) => {
      const key = workItemDetailKey(slug, vars.id);
      await qc.cancelQueries({ queryKey: key });
      const previous = qc.getQueryData<WorkItemDetail>(key);
      if (previous) {
        const merged = { ...previous } as Record<string, unknown>;
        for (const [k, v] of Object.entries(vars.edits)) {
          if (v !== undefined) merged[k] = v;
        }
        qc.setQueryData(key, merged as unknown as WorkItemDetail);
      }
      return { previous };
    },
    onError: (_e, vars, ctx) => {
      if (ctx?.previous) qc.setQueryData(workItemDetailKey(slug, vars.id), ctx.previous);
    },
    onSuccess: (_d, vars) => invalidate(vars.id),
  });
}

export function useToggleChecklistItem(slug: string) {
  const invalidate = useInvalidate(slug);
  return useMutation({
    mutationFn: (vars: { id: string; itemId: string; done: boolean }) =>
      workItemsApi.toggleChecklistItem(slug, vars.id, vars.itemId, vars.done),
    onSuccess: (_d, vars) => invalidate(vars.id),
  });
}

export function useSetChecklistNote(slug: string) {
  const invalidate = useInvalidate(slug);
  return useMutation({
    mutationFn: (vars: { id: string; itemId: string; note: string | null }) =>
      workItemsApi.setChecklistNote(slug, vars.id, vars.itemId, vars.note),
    onSuccess: (_d, vars) => invalidate(vars.id),
  });
}

// Template v2 (0124): set an allows_link item's URL (the document-folder links).
export function useSetChecklistLink(slug: string) {
  const invalidate = useInvalidate(slug);
  return useMutation({
    mutationFn: (vars: { id: string; itemId: string; linkUrl: string | null }) =>
      workItemsApi.setChecklistLink(slug, vars.id, vars.itemId, vars.linkUrl),
    onSuccess: (_d, vars) => invalidate(vars.id),
  });
}

// 0159: fill/clear a typed (date/text) checklist item's value — filled = done.
export function useSetChecklistValue(slug: string) {
  const invalidate = useInvalidate(slug);
  return useMutation({
    mutationFn: (vars: { id: string; itemId: string; value: string | null }) =>
      workItemsApi.setChecklistValue(slug, vars.id, vars.itemId, vars.value),
    onSuccess: (_d, vars) => invalidate(vars.id),
  });
}

// Polish fix #1: correct a done item's completion date (done_at; YYYY-MM-DD).
export function useSetChecklistDoneDate(slug: string) {
  const invalidate = useInvalidate(slug);
  return useMutation({
    mutationFn: (vars: { id: string; itemId: string; doneAt: string }) =>
      workItemsApi.setChecklistDoneDate(slug, vars.id, vars.itemId, vars.doneAt),
    onSuccess: (_d, vars) => invalidate(vars.id),
  });
}

export function useArchiveWorkItem(slug: string) {
  const invalidate = useInvalidate(slug);
  return useMutation({
    mutationFn: (id: string) => workItemsApi.archive(slug, id),
    onSuccess: (_d, id) => invalidate(id),
  });
}

export function useCreateWorkItem(slug: string) {
  const invalidate = useInvalidate(slug);
  return useMutation({
    mutationFn: (input: CreateWorkItemInput): Promise<WorkItemRow> => workItemsApi.create(slug, input),
    onSuccess: () => invalidate(),
  });
}

// Workspace v3 Phase 2 — recurrence.
export function useCreateRecurringSeries(slug: string) {
  const invalidate = useInvalidate(slug);
  return useMutation({
    mutationFn: (input: CreateRecurringSeriesInput): Promise<CreateRecurringSeriesResult> =>
      recurringSeriesApi.create(slug, input),
    onSuccess: () => invalidate(),
  });
}

export function useCompleteRecurringTask(slug: string) {
  const invalidate = useInvalidate(slug);
  return useMutation({
    mutationFn: (id: string): Promise<WorkItemDetail> => workItemsApi.completeRecurring(slug, id),
    onSuccess: (_d, id) => invalidate(id),
  });
}

export function usePauseRecurringSeries(slug: string) {
  const invalidate = useInvalidate(slug);
  return useMutation({
    mutationFn: (id: string): Promise<RecurringSeriesRow> => recurringSeriesApi.pause(slug, id),
    onSuccess: () => invalidate(),
  });
}

// Workspace v3.x — load one rule to prefill the drawer "Edit repeat" picker.
export function useRecurringSeries(slug: string, id: string | null, opts: { enabled?: boolean } = {}) {
  return useQuery<RecurringSeriesRow>({
    queryKey: ['recurring-series', slug, id ?? ''],
    queryFn: ({ signal }) => recurringSeriesApi.get(slug, id!, signal),
    staleTime: STALE_MS,
    enabled: (opts.enabled ?? true) && id !== null,
  });
}

// Workspace v3.x — edit a rule. A cadence change re-derives the schedule server-side
// (next_due + the live instance's due_date), so invalidate the whole workspace.
export function useUpdateRecurringSeries(slug: string) {
  const invalidate = useInvalidate(slug);
  return useMutation({
    mutationFn: (vars: { id: string; edits: UpdateRecurringSeriesInput }): Promise<RecurringSeriesRow> =>
      recurringSeriesApi.update(slug, vars.id, vars.edits),
    onSuccess: () => invalidate(),
  });
}

export function useAddComment(slug: string) {
  const invalidate = useInvalidate(slug);
  return useMutation({
    mutationFn: (vars: { id: string; body: string }) =>
      workItemsApi.addEvent(slug, vars.id, { kind: 'comment', body: vars.body }),
    onSuccess: (_d, vars) => invalidate(vars.id),
  });
}

export function useEditEvent(slug: string) {
  const invalidate = useInvalidate(slug);
  return useMutation({
    mutationFn: (vars: { id: string; eventId: string; body: string }) =>
      workItemsApi.editEvent(slug, vars.id, vars.eventId, vars.body),
    onSuccess: (_d, vars) => invalidate(vars.id),
  });
}

export function useSetEventEnteredOn(slug: string) {
  const invalidate = useInvalidate(slug);
  return useMutation({
    mutationFn: (vars: { id: string; eventId: string; enteredOn: string }) =>
      workItemsApi.setEventEnteredOn(slug, vars.id, vars.eventId, vars.enteredOn),
    onSuccess: (_d, vars) => invalidate(vars.id),
  });
}

export function useDeleteEvent(slug: string) {
  const invalidate = useInvalidate(slug);
  return useMutation({
    mutationFn: (vars: { id: string; eventId: string }) => workItemsApi.deleteEvent(slug, vars.id, vars.eventId),
    onSuccess: (_d, vars) => invalidate(vars.id),
  });
}

// Shared assignment (0108) — add/remove a collaborator. Invalidate refreshes the
// card stack (list), the drawer (detail), and the added user's bell feed.
export function useAddCollaborator(slug: string) {
  const invalidate = useInvalidate(slug);
  return useMutation({
    mutationFn: (vars: { id: string; userId: string }) => workItemsApi.addCollaborator(slug, vars.id, vars.userId),
    onSuccess: (_d, vars) => invalidate(vars.id),
  });
}

export function useRemoveCollaborator(slug: string) {
  const invalidate = useInvalidate(slug);
  return useMutation({
    mutationFn: (vars: { id: string; userId: string }) => workItemsApi.removeCollaborator(slug, vars.id, vars.userId),
    onSuccess: (_d, vars) => invalidate(vars.id),
  });
}

// Workspace v3.x — hard-delete a one-off task (tasks only; the drawer offers
// recurring tasks "Stop repeating" via usePauseRecurringSeries instead).
export function useDeleteTask(slug: string) {
  const invalidate = useInvalidate(slug);
  return useMutation({
    mutationFn: (id: string): Promise<void> => workItemsApi.deleteTask(slug, id),
    onSuccess: () => invalidate(),
  });
}

// 0155 — hard-delete an UNCONVERTED intake (the founder's pre-client-setup
// duplicate-cleanup affordance). Same DELETE dispatcher endpoint as
// useDeleteTask; the server routes by kind. A distinct FE-facing hook keeps
// the call sites (and their confirm copy) unambiguous about which kind of
// card they're deleting. A CONVERTED intake's client is archived instead
// (useSetClientArchived, clientsApi.ts) — never deleted.
export function useDeleteIntake(slug: string) {
  const invalidate = useInvalidate(slug);
  return useMutation({
    mutationFn: (id: string): Promise<void> => workItemsApi.deleteIntake(slug, id),
    onSuccess: () => invalidate(),
  });
}

// 0160 — hard-delete a process-board card (candidate cleanup on hiring-style
// boards). Same DELETE dispatcher endpoint; the server routes by kind. A
// distinct hook for the same reason as useDeleteIntake: unambiguous call
// sites + confirm copy per kind.
export function useDeleteProcessItem(slug: string) {
  const invalidate = useInvalidate(slug);
  return useMutation({
    mutationFn: (id: string): Promise<void> => workItemsApi.deleteProcessItem(slug, id),
    onSuccess: () => invalidate(),
  });
}

// Workspace v3.x fast-follow — turn a one-off task recurring (attach in place).
export function useAttachRecurrence(slug: string) {
  const invalidate = useInvalidate(slug);
  return useMutation({
    mutationFn: (vars: { id: string; input: AttachRecurrenceInput }): Promise<CreateRecurringSeriesResult> =>
      workItemsApi.attachRecurrence(slug, vars.id, vars.input),
    onSuccess: (_d, vars) => invalidate(vars.id),
  });
}
